import { useEffect, useRef } from 'preact/hooks'
import type { Content, ImageContent } from '../types'
import { replaceContents } from '../utils/contents'
import { Button, ButtonRow } from './Button'
import { Field } from './Field'
import { Panel } from './Panel'
import { SliderRow } from './SliderRow'

interface Props {
  value: Content[]
  onChange: (contents: Content[]) => void
}

const IMAGE_TRANSFORM_RESET = {
  x: 0,
  y: 0,
  scale: 1,
  rotation: 0,
} as const

const loadImage = (file: File): Promise<{ image: HTMLImageElement; url: string }> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      resolve({ image, url })
    }

    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Image load failed'))
    }

    image.src = url
  })
}

export function ImageUploader({ value, onChange }: Props) {
  const previousUrl = useRef<string | null>(null)

  const image = value.find((item): item is ImageContent => item.type === 'image') ?? null

  const revokePreviousUrl = () => {
    if (previousUrl.current) {
      URL.revokeObjectURL(previousUrl.current)
      previousUrl.current = null
    }
  }

  const updateImage = (next: ImageContent) => {
    onChange(replaceContents(value, 'image', [next], 'start'))
  }

  const setImage = (img: HTMLImageElement, url: string) => {
    revokePreviousUrl()
    previousUrl.current = url
    onChange(replaceContents(value, 'image', [{ type: 'image', image: img, ...IMAGE_TRANSFORM_RESET }], 'start'))
  }

  const clearImage = () => {
    revokePreviousUrl()
    onChange(replaceContents(value, 'image', []))
  }

  const onFile = async (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (file) {
      const { image: img, url } = await loadImage(file)

      setImage(img, url)
    }
  }

  useEffect(() => {
    return revokePreviousUrl
  }, [])

  return (
    <Panel title="Image" description="Drop in your app's artwork. Drag the preview to position it.">
      <Field>
        <input type="file" accept="image/*" onChange={onFile} />
      </Field>
      {image && (
        <>
          <SliderRow
            label="Scale"
            min={0.2}
            max={2}
            step={0.01}
            value={image.scale}
            display={image.scale.toFixed(2)}
            onChange={(scale) => updateImage({ ...image, scale })}
          />
          <SliderRow
            label="Rotate"
            min={0}
            max={360}
            value={image.rotation}
            display={`${image.rotation}°`}
            onChange={(rotation) => updateImage({ ...image, rotation })}
          />
          <ButtonRow>
            <Button variant="secondary" onClick={() => updateImage({ ...image, ...IMAGE_TRANSFORM_RESET })}>
              Reset
            </Button>
            <Button variant="secondary" onClick={clearImage}>
              Clear
            </Button>
          </ButtonRow>
        </>
      )}
    </Panel>
  )
}
